import { Injectable } from '@angular/core';
import { UserSettings } from '../model/user-settings';
import { UserDataService } from './user-data.service';
import { Observable, of } from 'rxjs';
import { MdmGroupVO } from '../model/vo/user.vo';
import { UserModel } from '../model/user.model';
import { UserUtils } from './user-utils';

@Injectable({
  providedIn: 'root'
})
export class UserMdmService {
  constructor(
    private readonly userModel: UserModel,
    private readonly userDataService: UserDataService,
    private readonly userSettings: UserSettings,
  ) {}

  public get canEditMdmGroupForPdmExport(): boolean {
    return UserUtils.hasPermissionToChangeMdmGroupForPdmExport(this.userModel.user);
  }

  public getMdmGroupsAvailableForPdmExport(): Observable<MdmGroupVO[]> {
    return this.canEditMdmGroupForPdmExport ? this.userDataService.getAllMdmGroups() : of([]);
  }

  public getPreselectedMdmGroupForPdmExport(allMdmGroups: MdmGroupVO[]): MdmGroupVO | undefined {
    // Get the mdmGroup selected by the admin
    let result = this.userModel.user.mdmGroup;

    if (this.canEditMdmGroupForPdmExport) {
      // User can change the mdmGroup, get the last selected value from userSettings.
      const lastSelectedMdmGroupId = this.getLastSelectedMdmGroupIdForPdmExport();
      if (lastSelectedMdmGroupId) {
        const lastSelectedMdmGroup = this.findMdmGroupById(allMdmGroups, lastSelectedMdmGroupId);
        if (lastSelectedMdmGroup) {
          result = lastSelectedMdmGroup;
        }
      }
    }

    return result;
  }

  private findMdmGroupById(allMdmGroups: MdmGroupVO[], id: number): MdmGroupVO | undefined {
    const groups = allMdmGroups ?? [];
    return groups.find(group => group.id === id);
  }

  public saveLastSelectedMdmGroupForPdmExport(mdmGroup: MdmGroupVO): void {
    if (mdmGroup && UserUtils.hasPermissionToChangeMdmGroupForPdmExport(this.userModel.user)) {
      this.setLastSelectedMdmGroupIdForPdmExport(mdmGroup.id);
    } else {
      this.deleteLastSelectedMdmGroupIdForPdmExport();
    }
  }

  private getLastSelectedMdmGroupIdForPdmExport(): number {
    return this.userSettings.getUserSettingNumberValue("lastSelectedMdmGroupIdForPdmExport");
  }

  private setLastSelectedMdmGroupIdForPdmExport(mdmGroupId: number): void {
    this.userSettings.setUserSettingValue("lastSelectedMdmGroupIdForPdmExport", mdmGroupId);
    this.userSettings.saveUserSettings();
  }

  private deleteLastSelectedMdmGroupIdForPdmExport(): void {
    if (this.getLastSelectedMdmGroupIdForPdmExport() !== null) {
      this.userSettings.deleteUserSettingValue("lastSelectedMdmGroupIdForPdmExport");
      this.userSettings.saveUserSettings();
    }
  }
}
